import * as React from "react";
import { ReactNode } from "react";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableHead from "@material-ui/core/TableHead";
import Table from "@material-ui/core/Table";
import styles from "./styles.module.scss";
import TableBody from "@material-ui/core/TableBody";
import { SEPagination } from "./SEPagination";
import { BaSeLoading } from "@base/react";

export type SETableColumnsProps = {
  id: string;
  label: string;
  icon?: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  minWidth?: number | string;
  width?: number | string;
  orderBy?: string;
  cellStyle?: {};
  sortable?: boolean;
  columnStyle?: {};
  customLabel?: (label: any) => ReactNode;
  customField?: (value: any, row: any) => ReactNode;
};

interface SETableProps {
  columns: any;
  data: any[];
  defaultOrderBy?: string;
  defaultDirection?: string;
  direction?: string;
  headerTableColor?: string | undefined;
  lineColor?: string;
  linesPerPageFilters?: number | string;
  linesPerPageColor?: string;
  onInputPageChange?: (page: number) => void;
  onKeyPressChange?: (key: string) => void;
  onLinesPerPageChange?: (line: string) => void;
  onOrderChange?: (order: { orderBy: string; direction: string }) => void;
  onPageNextChange?: (page: number) => void;
  onPagePreviousChange?: (page: number) => void;
  orderBy?: string;
  page?: number;
  pagination?: boolean;
  tableColor?: string | undefined;
  totalPages?: number;
  isLoading?: boolean;
  totalElements?: number;
  tableHeader?: ReactNode;
}

export const SETable: React.FC<SETableProps> = ({
  columns,
  data,
  defaultOrderBy = "id",
  defaultDirection = "DESC",
  direction = "DESC",
  headerTableColor = "#ebebf0",
  lineColor = "#fbfaff",
  linesPerPageFilters = 10,
  linesPerPageColor = "#555770",
  onInputPageChange = () => {},
  onKeyPressChange = () => {},
  onLinesPerPageChange = () => {},
  onOrderChange,
  onPageNextChange = () => {},
  onPagePreviousChange = () => {},
  orderBy = "id",
  page = 0,
  isLoading,
  pagination = true,
  tableColor = "#8f90a6",
  totalPages,
  totalElements,
  tableHeader,
}) => {
  const handleRequestSort = (property: string) => {
    if (onOrderChange) {
      const isAsc = direction === "ASC";
      if (property === orderBy) {
        if (!isAsc) {
          onOrderChange({orderBy: property, direction: 'DESC'});
        } else if (isAsc) {
            onOrderChange({ orderBy: property, direction: 'ASC' });
        } else {
          onOrderChange({
            orderBy: defaultOrderBy,
            direction: defaultDirection,
          });
        }
      } else {
        onOrderChange({ orderBy: property, direction: "ASC" });
      }
    }
  };

  const renderTableHead = (columns: SETableColumnsProps[]) => {
    return (
      <TableHead>
        <TableRow>
          {columns.map((column: SETableColumnsProps, index) => {
            const orderByCollumn = column?.orderBy || column?.id;
            return (
              <TableCell
                key={index}
                align={column?.align}
                style={{
                  padding: "8px 16px",
                  width: column?.width,
                  minWidth: column?.minWidth,
                  backgroundColor: headerTableColor,
                  fontFamily: "Mulish, sans-serif",
                  fontSize: 13,
                  fontWeight: "600",
                  border: "none",
                  ...column?.cellStyle,
                }}
              >
                {!!column.customLabel ? (
                  column.customLabel(column.label)
                ) : (
                  <div
                    className={styles.tableCellContainer}
                    style={{ justifyContent: column?.align }}
                  >
                    <p
                      className={styles.titleTable}
                      style={{ color: tableColor }}
                    >
                      {column.label}
                    </p>
                    {column?.sortable === false ? (
                      <></>
                    ) : (
                      <i
                        className={
                          orderBy === orderByCollumn && direction === "ASC"
                            ? "ri-arrow-up-line"
                            : orderBy === orderByCollumn && direction === "DESC"
                            ? "ri-arrow-down-line"
                            : "ri-arrow-up-down-line"
                        }
                        style={{
                          color: tableColor,
                          fontSize: 15,
                          cursor: "pointer",
                        }}
                        onClick={() => handleRequestSort(orderByCollumn)}
                      ></i>
                    )}
                  </div>
                )}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  };

  const renderTableRows = (row: any, columns: SETableColumnsProps[], rowIndex: number) => {
    return columns.map((column: SETableColumnsProps, columnIndex) => {
      const { id } = column;
      const value = row[id];
      return (
        <TableCell
          key={`row-${rowIndex}-col-${columnIndex}-${id}`}
          align={column?.align}
          style={{
            color: "#555770",
            fontFamily: "Mulish, sans-serif",
            border: "1px solid #EBEBF0",
          }}
        >
          {!!column.customField ? column.customField(value, row) : <>{value}</>}
        </TableCell>
      );
    });
  };

  return (
    <Paper elevation={2} sx={{ padding: 2 }}>
      {tableHeader}
      <TableContainer
        component={Paper}
        style={{
          borderRadius: "8px",
        }}
      >
        <Table>
          {renderTableHead(columns)}
          <TableBody>
            {!!isLoading ? (
              <TableRow style={{ backgroundColor: "#FFFF" }}>
                <TableCell align={"center"} colSpan={columns?.length}>
                  <BaSeLoading text={""} spinnerSize={48} />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {!data?.length ? (
                  <TableRow style={{ backgroundColor: "#FFFF" }}>
                    <TableCell align={"center"} colSpan={columns?.length}>
                      <p className={styles.tableEmptyText}>
                        Não existem resultados para a sua busca
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row, index) => (
                    <TableRow
                      style={{
                        backgroundColor: index % 2 === 0 ? "#fff" : lineColor,
                      }}
                      hover
                      tabIndex={-1}
                      key={index}
                    >
                      {renderTableRows(row, columns, index)}
                    </TableRow>
                  ))
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <SEPagination
          color={linesPerPageColor}
          linesPerPageFilters={linesPerPageFilters}
          linesPerPageOptions={[10, 25, 50, 100]}
          totalPages={totalPages}
          currentPage={page}
          onLinesPerPageChange={onLinesPerPageChange}
          onPagePreviousChange={onPagePreviousChange}
          onPageNextChange={onPageNextChange}
          onInputPageChange={onInputPageChange}
          onKeyPressChange={onKeyPressChange}
        />
      )}
    </Paper>
  );
};
